// frontend/src/hooks/usePoliciesData.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from '../utils/toast';
import { configAPI } from '../services/api';

/** All published company policies for the library hub, with the employee's
 * acknowledgement status on each. */
export const usePolicies = () => {
  return useQuery({
    queryKey: ['policies', 'list'],
    queryFn: async () => {
      const response = await configAPI.getPolicies();
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // policies change rarely
    retry: 1,
  });
};

/** A single published policy by key (meta + blocks + version history + ack). */
export const usePolicy = (policyKey) => {
  return useQuery({
    queryKey: ['policies', 'detail', policyKey],
    queryFn: async () => {
      const response = await configAPI.getPolicy(policyKey);
      return response.data;
    },
    enabled: !!policyKey,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/** Sign off the current published revision of one policy. */
export const useAcknowledgePolicy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policyKey) => configAPI.acknowledgePolicy(policyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success('Policy acknowledged. Thank you!');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Could not record acknowledgement');
    },
  });
};

/** Download the signed master copy (controlled PDF) of a policy. */
export const useDownloadPolicyPdf = () => {
  return useMutation({
    mutationFn: async ({ policyKey, title, version }) => {
      const response = await configAPI.getPolicySignedPdf(policyKey);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const safe = (title || policyKey).replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safe}${version ? `_v${version}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    },
    onError: () => {
      toast.error('Could not download the signed copy.');
    },
  });
};

/** HR compliance dashboard: per-policy acknowledgement matrix across employees. */
export const usePolicyAckDashboard = () => {
  return useQuery({
    queryKey: ['policies', 'acknowledgement', 'dashboard'],
    queryFn: async () => {
      const response = await configAPI.getPolicyAckDashboard();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

export default usePolicies;
